import { ChevronLeft, ChevronRight, Columns2 } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useLocationAccess } from '@/hooks/useLocationAccess'
import { NETWORK_COLOR } from '@/lib/locationColors'
import { cn } from '@/lib/utils'
import { BrandLogo } from './BrandLogo'
import { LocationContextDropdown } from './LocationContextDropdown'
import { SidebarMenu } from './SidebarMenu'
import { UserFooter } from './UserFooter'

export function MainSidebar() {
  const { mainState, railVisible, cycleSidebar, setMainState, toggleRail } = useSidebarStore()
  const { hasMultipleLocations } = useLocationAccess()
  const { pathname } = useLocation()

  // Sidebar can only be fully hidden on the ramp page
  const isRampPage = pathname.endsWith('/ramp')
  const effectiveState = (!isRampPage && mainState === 'hidden') ? 'collapsed' : mainState

  // Hide logo when IconRail is visible (it already shows the logo icon)
  const showLogo = !(hasMultipleLocations && railVisible)

  if (effectiveState === 'hidden') return null

  const isCollapsed = effectiveState === 'collapsed'

  return (
    <div
      className={cn(
        'flex shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300',
        isCollapsed ? 'w-[50px]' : 'w-[232px]'
      )}
    >
      {/* Header: Brand + rail toggle + collapse toggle */}
      <div
        className={cn(
          'flex shrink-0 py-3',
          isCollapsed ? 'flex-col items-center gap-1 px-0' : 'items-center justify-between px-3.5'
        )}
      >
        <BrandLogo size="md" showText={!isCollapsed} showLogo={showLogo} />
        <div className={cn('flex items-center gap-0.5', isCollapsed && 'flex-col')}>
          {hasMultipleLocations && (
            <button
              onClick={toggleRail}
              className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent"
              title={railVisible ? 'Hide location rail' : 'Show location rail'}
            >
              <Columns2
                className={cn('h-3.5 w-3.5', !railVisible && 'text-muted-foreground')}
                style={railVisible ? { color: NETWORK_COLOR } : undefined}
                strokeWidth={1.6}
              />
            </button>
          )}
          <button
            onClick={
              isCollapsed
                ? () => setMainState('expanded')
                : isRampPage
                  ? cycleSidebar
                  : () => setMainState('collapsed')
            }
            className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Location context dropdown */}
      <LocationContextDropdown collapsed={isCollapsed} />

      {/* Navigation menu */}
      <SidebarMenu collapsed={isCollapsed} />

      {/* User footer */}
      <UserFooter collapsed={isCollapsed} />
    </div>
  )
}
